/**
 * channels.js — Registro CANÔNICO de canais/transportes.
 *
 * Fonte ÚNICA de `label` + `color` (+ tint derivado) por canal. Antes cada tela
 * mantinha seu próprio mapa inline (ConnectionIcon, TicketListItemCustom,
 * dashboards…), o que gerou divergências reais — ex.: o azul do Facebook ficou
 * `#3b5998` (pré-2019) num mapa e `#1877F2` (atual) no outro. Centralizar mata
 * essa classe de bug e garante que ícone, chip e label falem a MESMA identidade.
 *
 * Convenção:
 *  - Transportes de WhatsApp (whatsapp/waba/wuzapi/whatsmeow) são DISTINTOS de
 *    propósito → cores distintas por natureza técnica (verde não-oficial, azul
 *    API oficial, teal microserviço próprio).
 *  - `color` = cor de MARCA/identidade, usada em ícone (ConnectionIcon) e no
 *    fundo/texto do <ChannelChip>.
 *
 * ⚠️ NÃO confundir com `Dashboard/constants/dashboardTheme.js#CHANNEL_COLORS`:
 * aquilo são cores de SÉRIE de gráfico (legibilidade em pizza/linha, ex.: email
 * = slate `#64748b`), com restrições diferentes das cores de marca daqui. São
 * intencionalmente separadas.
 */

// key -> { label, color }
export const CHANNELS = {
  whatsapp: { label: "WhatsApp", color: "#25D366" }, // Baileys (não oficial)
  waba: { label: "API Oficial", color: "#3B82F6" }, // WhatsApp Business API (Meta)
  wuzapi: { label: "WhatsApp", color: "#25D366" }, // whatsmeow via WuzAPI
  whatsmeow: { label: "WhatsApp", color: "#00BFA5" }, // microserviço Go próprio
  instagram: { label: "Instagram", color: "#E1306C" },
  facebook: { label: "Facebook", color: "#1877F2" }, // azul de marca atual (pós-2019)
  telegram: { label: "Telegram", color: "#0088CC" },
  email: { label: "Email", color: "#EA4335" }, // vermelho Gmail
  webchat: { label: "Webchat", color: "#6366f1" }, // indigo (casa com o dashboard)
};

// Fallback neutro (slate) — mesmo tom do TagChip, p/ canais desconhecidos.
const FALLBACK = { label: "", color: "#64748b" };

export const getChannelMeta = (channel) => CHANNELS[channel] || FALLBACK;

export const getChannelLabel = (channel) => {
  if (!channel) return "";
  return CHANNELS[channel]?.label || String(channel).toUpperCase();
};

export const getChannelColor = (channel) => getChannelMeta(channel).color;

// #RRGGBB (ou #RGB) -> rgba(...) — p/ fundos "tintados" de chips/badges.
const hexToRgba = (hex, alpha) => {
  const h = String(hex).replace("#", "");
  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const CHANNEL_TINT_ALPHA = 0.12;

export const getChannelTint = (channel, alpha = CHANNEL_TINT_ALPHA) =>
  hexToRgba(getChannelColor(channel), alpha);

// Estilo de badge tintado (fundo suave + cor cheia da marca). Reservado p/ chips
// inline que queiram o par {background,color} pronto sem passar pelo <ChannelChip>.
export const getChannelStyle = (channel, alpha = CHANNEL_TINT_ALPHA) => ({
  background: getChannelTint(channel, alpha),
  color: getChannelColor(channel),
});
